import { useEffect, useMemo, useRef, useState } from 'react'
import { CornerDownLeft, Search } from 'lucide-react'

function normalise(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

// Subsequence match, so "mulh" and "spmul" both find Mulhacén in Spain.
function matches(haystack, needle) {
  if (!needle) return true
  const target = normalise(haystack)
  const query = normalise(needle)
  if (target.includes(query)) return true
  let index = 0
  for (const character of query) {
    index = target.indexOf(character, index)
    if (index === -1) return false
    index += 1
  }
  return true
}

export default function CommandPalette({ open, onClose, items }) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const filtered = useMemo(
    () => items.filter((item) => matches(`${item.label} ${item.sublabel || ''} ${item.keywords || ''}`, query)).slice(0, 40),
    [items, query],
  )

  useEffect(() => {
    if (!open) return
    setQuery('')
    setActiveIndex(0)
    const timer = window.setTimeout(() => inputRef.current?.focus(), 20)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => { setActiveIndex(0) }, [query])

  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active="true"]')
    active?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, filtered.length])

  if (!open) return null

  function choose(item) {
    onClose()
    item.run()
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => (filtered.length ? (current + 1) % filtered.length : 0))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => (filtered.length ? (current - 1 + filtered.length) % filtered.length : 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const item = filtered[activeIndex]
      if (item) choose(item)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }

  let lastGroup = null

  return (
    <div className="palette-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="palette__input">
          <Search size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Jump to a summit, trip or view…"
            aria-label="Search summits, trips and commands"
            aria-controls="palette-results"
          />
          <kbd>Esc</kbd>
        </div>

        <div className="palette__results" id="palette-results" ref={listRef} role="listbox">
          {!filtered.length && <div className="palette__empty">Nothing matches “{query}”.</div>}
          {filtered.map((item, index) => {
            const showGroup = item.group !== lastGroup
            lastGroup = item.group
            return (
              <div key={item.id}>
                {showGroup && <div className="palette__group">{item.group}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  data-active={index === activeIndex}
                  className={index === activeIndex ? 'active' : ''}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(item)}
                >
                  <span className="palette__icon">{item.icon}</span>
                  <span className="palette__copy">
                    <strong>{item.label}</strong>
                    {item.sublabel && <small>{item.sublabel}</small>}
                  </span>
                  {index === activeIndex && <CornerDownLeft size={14} />}
                </button>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
